import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { useTranslation } from "@/i18n";

const goContact = () => {
  const target = document.querySelector("#contact");
  if (!target) return;
  if (window.__lenis) window.__lenis.scrollTo(target, { offset: -72, duration: 1.4 });
  else target.scrollIntoView({ behavior: "smooth" });
};

export default function Offres() {
  const { t } = useTranslation();

  const packs = [
    {
      id: "missions",
      code: "01",
      title: t("offres.p1.title"),
      tagline: t("offres.p1.tagline"),
      points: t("offres.p1.points"),
      note: t("offres.p1.note"),
    },
    {
      id: "abonnement",
      code: "02",
      title: t("offres.p2.title"),
      tagline: t("offres.p2.tagline"),
      points: t("offres.p2.points"),
      note: t("offres.p2.note"),
    },
    {
      id: "packs",
      code: "03",
      title: t("offres.p3.title"),
      tagline: t("offres.p3.tagline"),
      points: t("offres.p3.points"),
      note: t("offres.p3.note"),
    },
  ];

  return (
    <section id="offres" className="relative py-24 sm:py-32 lg:py-40 bg-ink" data-testid="offres-section">
      <div className="max-w-[1400px] mx-auto px-6 sm:px-12 lg:px-20">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        >
          <span className="label-accent" data-testid="offres-overline">{t("offres.overline")}</span>
          <h2 className="mt-6 font-serif font-light tracking-tight text-3xl sm:text-4xl lg:text-5xl max-w-3xl leading-tight">
            {t("offres.h2a")}
            <em className="italic text-gold-gradient not-italic">{t("offres.h2em")}</em>.
          </h2>
          <p className="mt-6 text-base text-[#FAF8F5]/60 max-w-2xl leading-relaxed">
            {t("offres.side")}
          </p>
        </motion.div>

        <div className="mt-16 sm:mt-20 grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
          {packs.map((p, i) => (
            <motion.article
              key={p.id}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.9, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] }}
              whileHover={{ y: -6 }}
              className="group relative bg-ink-card border border-white/5 hover:border-gold/40 p-8 sm:p-10 transition-colors duration-500 flex flex-col"
              data-testid={`offre-card-${p.id}`}
            >
              <span className="font-mono text-[11px] tracking-[0.3em] text-gold/70">{p.code}</span>
              <h3 className="mt-4 font-serif font-light text-2xl sm:text-3xl">{p.title}</h3>
              <p className="mt-3 font-serif italic text-gold-gradient text-base sm:text-lg leading-snug">
                {p.tagline}
              </p>
              <ul className="mt-6 space-y-3 flex-1">
                {p.points.map((pt) => (
                  <li key={pt} className="flex gap-3 text-sm text-[#FAF8F5]/70 leading-relaxed">
                    <span className="text-gold mt-0.5 shrink-0">—</span>
                    {pt}
                  </li>
                ))}
              </ul>
              <p className="mt-6 pt-6 border-t border-white/5 text-xs text-[#FAF8F5]/70 leading-relaxed">
                {p.note}
              </p>
              <button
                onClick={goContact}
                className="mt-6 inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-gold hover:gap-3 transition-all duration-300"
                data-testid={`offre-cta-${p.id}`}
              >
                {t("offres.cta")} <ArrowUpRight size={14} />
              </button>
            </motion.article>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="mt-20 sm:mt-28"
          data-testid="offres-tarification"
        >
          <span className="label-accent">{t("offres.tarifLabel")}</span>
          <p className="mt-6 font-serif font-light text-2xl sm:text-3xl lg:text-4xl leading-tight max-w-3xl">
            {t("offres.tarifH2a")}
            <em className="italic text-gold-gradient not-italic">{t("offres.tarifH2em")}</em>
          </p>
          <p className="mt-8 text-base text-[#FAF8F5]/60 max-w-3xl leading-relaxed" data-testid="tarif-texte">
            {t("offres.tarifTexte")}
          </p>

          <p className="mt-12 pl-5 border-l-2 border-gold/60 text-sm text-[#FAF8F5]/70 max-w-2xl leading-relaxed" data-testid="tarif-accreditation">
            {t("offres.tarifAccreditation")}
          </p>
        </motion.div>
      </div>
    </section>
  );
}
